import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownHandles } from '../../../components/Dropdown';
import {
  FeedPostContainer,
  FeedContainer,
  FilterContainer,
  DropdownContainer,
  FilterWrapper,
  FeedWrapper,
  PaginationButton,
  PageInfo,
  PaginationContainer,
  SearchWrapper,
  SearchInput,
} from './styles';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
} from '../../../assets/svg/SVGComponents';
import PostProjectComponent from '../../../components/PostProjectComponent';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import IRequestError from '../../../dtos/IRequestError';
import { useToast } from '../../../hooks/toast';
import { IPostProjectProps } from '../../../dtos/IPostProjectProps';
import NoPostProject from '../NoPostProject';
import { filterToSortTypeTexts, SortType } from '../../../utils/enums';
import LoadingStateFeed from '../LoadingStateFeed';
import Input from '../../../components/Input';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';

interface ITypeOfFeedProps {
  type: 'projects' | 'my_projects';
}
const FeedPost: React.FC<ITypeOfFeedProps> = ({ type }) => {
  const { user, isSessionExpired } = useAuth();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [postProjects, setPostProject] = useState<IPostProjectProps[]>([]);
  const [selectedFilter, setSelectedFilter] = useState('Recentes');
  const filterDropdownRef = useRef<DropdownHandles>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const [currentFilter, setCurrentFilter] = useState(SortType.RECENT);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [postsPerPage] = useState(10);
  const [searchKeywords, setSearchKeywords] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [inputKeyword, setInputKeyword] = useState(false);

  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    setIsFilled(!!inputRef.current?.value);
  }, []);

  const loadPosts = useCallback(
    async (pageNumber = 1, sortBy = SortType.RECENT, keywords = '') => {
      setIsLoading(true);

      try {
        let response;
        if (type === 'projects') {
          response = await api.get('/posts/feed', {
            params: {
              sortBy,
              page: pageNumber,
              limit: postsPerPage,
              keywords: keywords.trim() !== '' ? keywords : undefined,
            },
          });
        } else if (type === 'my_projects') {
          response = await api.get('/posts/user_projects');
        }

        if (response?.data) {
          if (response.data?.error) {
            setIsEmpty(true);
            setPostProject([]);
            setTotalPages(1);
          } else {
            // Extrair posts considerando diferentes formatos de resposta da API
            let posts: IPostProjectProps[] = [];
            const { data } = response;

            if (Array.isArray(data)) {
              posts = data;
            } else if (data?.posts && Array.isArray(data.posts)) {
              posts = data.posts;
            } else if (data?.data && Array.isArray(data.data)) {
              posts = data.data;
            } else if (
              data?.user_projects &&
              Array.isArray(data.user_projects)
            ) {
              posts = data.user_projects;
            }

            setPostProject(posts);
            setIsEmpty(posts.length === 0);

            if (type === 'my_projects') {
              // my_projects não tem paginação no servidor
              setTotalPages(1);
            } else {
              // Calcular o total de páginas usando o header X-Total-Count se disponível
              const totalCount = response.headers['x-total-count'];
              if (totalCount) {
                setTotalPages(
                  Math.ceil(parseInt(totalCount, 10) / postsPerPage),
                );
              } else {
                // Se retornou menos que o limite, esta é a última página
                if (posts.length < postsPerPage) {
                  setTotalPages(pageNumber);
                } else {
                  // Pode haver mais páginas
                  setTotalPages(pageNumber + 1);
                }
              }
            }
          }
        }
      } catch (err) {
        if (!(await isSessionExpired(err as IRequestError))) {
          addToast({
            type: 'error',
            expression: 'Ops.',
            description: t('toasts.errors.load_data_fail'),
          });
        }
        setIsEmpty(true);
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, isSessionExpired, postsPerPage, t, type],
  );

  useEffect(() => {
    if (currentFilter !== SortType.KEYWORDS)
      loadPosts(currentPage, currentFilter);
    if (currentFilter === SortType.KEYWORDS && searchQuery !== '')
      loadPosts(currentPage, currentFilter, searchQuery);
  }, [currentFilter, currentPage, loadPosts, searchQuery]);

  // Limpar a pesquisa
  const handleClearSearch = useCallback(() => {
    setSearchKeywords('');
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  /**
   * Tira da lista o projeto excluído.
   *
   * Sem recarregar de propósito: recarregar reposicionaria a página e, na
   * última página, poderia deixá-la vazia enquanto ainda há posts atrás.
   */
  const handlePostDeleted = useCallback((post_id: string) => {
    setPostProject(previous => previous.filter(post => post.id !== post_id));
  }, []);

  const handleFilterClick = useCallback(
    (filter: SortType) => {
      setSelectedFilter(filterToSortTypeTexts[filter]);
      setCurrentFilter(filter);
      setCurrentPage(1); // Reset para a primeira página quando trocar o filtro
      filterDropdownRef.current?.closeDropdown();

      if (filter === SortType.KEYWORDS) {
        setInputKeyword(true);
      } else {
        setInputKeyword(false);
        handleClearSearch();
      }
      // Carregar posts com o novo filtro
      if (filter !== SortType.KEYWORDS) loadPosts(1, filter);
      if (filter === SortType.KEYWORDS && searchQuery !== '')
        loadPosts(1, filter, searchQuery);
    },
    [handleClearSearch, loadPosts, searchQuery],
  );

  const handleSearch = useCallback(() => {
    setSearchQuery(searchKeywords);
    setCurrentPage(1);
  }, [searchKeywords]);

  // Função para realizar a pesquisa ao pressionar Enter
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const handleClickSearchButton = useCallback(() => {
    handleSearch();
  }, [handleSearch]);

  // Funções para navegação de página
  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);
  return (
    <FeedPostContainer id="feed-community">
      <FeedContainer>
        <FilterContainer>
          <FilterWrapper>
            <h3>Filtrar por: </h3>
            <DropdownContainer>
              <button
                type="button"
                onClick={() => filterDropdownRef.current?.toggleDropdown()}
              >
                {selectedFilter}
                <ChevronDownIcon
                  strokeWidth={3}
                  stroke="#323232"
                  width="0.8em"
                />
              </button>
              <Dropdown ref={filterDropdownRef}>
                <ul>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleFilterClick(SortType.RECENT)}
                    >
                      Recentes
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleFilterClick(SortType.RELEVANT)}
                    >
                      Populares
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleFilterClick(SortType.MOST_COMMENTED)}
                    >
                      Comentados
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleFilterClick(SortType.KEYWORDS)}
                    >
                      Palavra Chave
                    </button>
                  </li>
                </ul>
              </Dropdown>
            </DropdownContainer>

            <SearchWrapper $inputKeyword={inputKeyword}>
              <SearchInput $isFilled={isFilled} $isFocused={isFocused}>
                <SearchIcon
                  width="1.33rem"
                  fill="none"
                  strokeWidth={2.5}
                  stroke="#1e88e5"
                />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Digite a palavra chave"
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  value={searchKeywords}
                  onChange={e => setSearchKeywords(e.target.value)}
                  onKeyDown={handleKeyPress}
                />
              </SearchInput>
              <PrimaryButton onClick={() => handleClickSearchButton()}>
                Pesquisar
              </PrimaryButton>
            </SearchWrapper>
          </FilterWrapper>
          {isLoading && <LoadingStateFeed />}
          {!isLoading && postProjects && postProjects.length > 0 && (
            <>
              <FeedWrapper>
                {postProjects &&
                  postProjects.map(post => (
                    <PostProjectComponent
                      id={post.id}
                      key={post.id}
                      user={post.user}
                      comments={post.comments}
                      description={post.description}
                      title={post.title}
                      tags={post.tags}
                      grade={post.grade}
                      hardware={post.hardware}
                      likes={post.likes}
                      is_liked_by_user={post.is_liked_by_user || false}
                      photos={post.photos}
                      date_creation={post.date_creation}
                      onDeleted={handlePostDeleted}
                    />
                  ))}
              </FeedWrapper>
            </>
          )}
          {type === 'projects' && totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton
                disabled={currentPage === 1}
                onClick={handlePrevPage}
                type="button"
              >
                <ChevronLeftIcon
                  strokeWidth={3}
                  stroke={currentPage === 1 ? '#AAAAAA' : '#323232'}
                  width="1rem"
                />
              </PaginationButton>

              <PageInfo>
                Página {currentPage} de {totalPages}
              </PageInfo>

              <PaginationButton
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                type="button"
              >
                <ChevronRightIcon
                  strokeWidth={3}
                  stroke={currentPage === totalPages ? '#AAAAAA' : '#323232'}
                  width="1rem"
                />
              </PaginationButton>
            </PaginationContainer>
          )}
        </FilterContainer>
      </FeedContainer>

      {!isLoading && (!postProjects || postProjects.length === 0) && (
        <NoPostProject />
      )}
    </FeedPostContainer>
  );
};

export default FeedPost;
